import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../services/api'
import { useCartStore } from '../store/cartStore'

const OrderPage = () => {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [requiresPhone, setRequiresPhone] = useState(false)
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const orderRef = useRef(null)
  const pollingIntervalRef = useRef(null)
  const clearCart = useCartStore((state) => state.clearCart)
  
  const API_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''
  
  // Нормализация номера телефона (оставляем только цифры)
  const normalizePhone = (phone) => {
    return phone.replace(/\D/g, '')
  }
  
  // Очищаем корзину при загрузке страницы заказа, если это возврат после оплаты
  useEffect(() => {
    const shouldClearCart = sessionStorage.getItem('shouldClearCart')
    const pendingOrderId = sessionStorage.getItem('pendingOrderId')
    
    if (shouldClearCart === 'true' && pendingOrderId && pendingOrderId === id) {
      clearCart()
      sessionStorage.removeItem('shouldClearCart')
      sessionStorage.removeItem('pendingOrderId')
    }
    
    // Также очищаем корзину, если заказ найден и его статус paid или cdek_created
    // Это дополнительная защита на случай, если sessionStorage не сработал
    if (order && (order.status === 'paid' || order.status === 'cdek_created')) {
      clearCart()
      // Очищаем sessionStorage, если он был установлен
      sessionStorage.removeItem('shouldClearCart')
      sessionStorage.removeItem('pendingOrderId')
    }
  }, [id, clearCart, order])

  const formatPrice = (value) => {
    return new Intl.NumberFormat('ru-RU').format(Number(value))
  }

  // Функция для загрузки заказа с номером телефона
  const fetchOrderWithPhone = async (phoneNumber = null) => {
    try {
      const url = phoneNumber 
        ? `/orders/orders/${id}/?phone=${encodeURIComponent(phoneNumber)}`
        : `/orders/orders/${id}/`
      
      const orderResponse = await api.get(url)
      const currentOrder = orderResponse.data
      setOrder(currentOrder)
      setError(null)
      setRequiresPhone(false)
      
      // Если заказ найден и оплачен, очищаем корзину
      if (currentOrder.status === 'paid' || currentOrder.status === 'cdek_created') {
        clearCart()
        sessionStorage.removeItem('shouldClearCart')
        sessionStorage.removeItem('pendingOrderId')
      }
      
      return currentOrder
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.requires_phone) {
        // Требуется номер телефона
        setRequiresPhone(true)
        setError(null)
        return null
      } else if (err.response?.status === 403) {
        // Неверный номер телефона
        setRequiresPhone(true)
        setPhoneError('Неверный номер телефона')
        return null
      } else if (err.response?.status === 404) {
        // Заказ не найден (возможно еще не создан)
        return null
      } else {
        setError('Ошибка загрузки заказа')
        console.error('Error fetching order:', err)
        return null
      }
    }
  }

  // Обработка ввода номера телефона
  const handlePhoneChange = (e) => {
    let value = e.target.value
    // Удаляем все кроме цифр и +
    value = value.replace(/[^\d+]/g, '')
    
    // Автоматически добавляем +7 для российских номеров
    if (value && !value.startsWith('+')) {
      if (value.startsWith('7') || value.startsWith('8')) {
        value = '+7' + value.slice(1)
      } else {
        value = '+7' + value
      }
    }
    
    // Ограничиваем длину
    if (value.startsWith('+7')) {
      if (value.length > 12) value = value.slice(0, 12)
    } else if (value.startsWith('+375')) {
      if (value.length > 13) value = value.slice(0, 13)
    }
    
    setPhone(value)
    setPhoneError('')
  }

  // Отправка формы с номером телефона
  const handlePhoneSubmit = async (e) => {
    e.preventDefault()
    
    if (!phone || phone.length < 10) {
      setPhoneError('Введите номер телефона')
      return
    }
    
    setPhoneError('')
    setLoading(true)
    
    const currentOrder = await fetchOrderWithPhone(phone)
    if (currentOrder) {
      setLoading(false)
      // Заказ загружен, дальше работает обычная логика
    } else {
      setLoading(false)
      if (!requiresPhone) {
        setError('Заказ не найден')
      }
    }
  }

  useEffect(() => {
    const fetchOrderStatus = async () => {
      // Делаем несколько попыток получить заказ (на случай если callback еще не обработан)
      const maxAttempts = 5
      let attempts = 0
      let orderFound = false
      
      while (attempts < maxAttempts && !orderFound && !requiresPhone) {
        const currentOrder = await fetchOrderWithPhone()
        
        if (currentOrder) {
          orderFound = true
          
          // Проверяем статус платежа и обновляем заказ
        try {
          await api.get(`/payments/payments/check/?order_id=${id}`)
            const updatedOrder = await fetchOrderWithPhone(phone || null)
            if (updatedOrder) {
              // Если заказ оплачен, очищаем корзину
              if (updatedOrder.status === 'paid' || updatedOrder.status === 'cdek_created') {
                clearCart()
                sessionStorage.removeItem('shouldClearCart')
                sessionStorage.removeItem('pendingOrderId')
              }
            }
        } catch (paymentErr) {
          console.log('Payment check skipped')
        }
          
          setLoading(false)
        } else if (requiresPhone) {
          // Требуется номер телефона, прерываем попытки
          setLoading(false)
          break
        } else {
          attempts++
          if (attempts < maxAttempts) {
            const delay = attempts * 1000
            console.log(`Заказ не найден, попытка ${attempts}/${maxAttempts}, повтор через ${delay/1000} сек...`)
            await new Promise(resolve => setTimeout(resolve, delay))
          } else {
        setError('Заказ не найден')
        setLoading(false)
            break
          }
        }
      }
    }

    if (!requiresPhone && !phone) {
    fetchOrderStatus()
    }
    
    // Обновляем заказ периодически (только если заказ уже загружен)
    if (order && (phone || !requiresPhone)) {
      pollingIntervalRef.current = setInterval(async () => {
        try {
          // Используем fetchOrderWithPhone для polling
          const currentOrder = await fetchOrderWithPhone(phone || null)
          if (!currentOrder) return // Если требует телефон, пропускаем polling
        
        // Не обновляем завершенные/отмененные заказы
        if (currentOrder?.status === 'delivered' || currentOrder?.status === 'cancelled') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          return
        }
        
        // Обновляем только если статус pending (ожидание оплаты)
        if (currentOrder?.status === 'pending') {
          // Проверяем статус платежа
          try {
            await api.get(`/payments/payments/check/?order_id=${id}`)
            const updatedOrder = await fetchOrderWithPhone(phone || null)
            if (updatedOrder) {
              // Если заказ оплачен, очищаем корзину
              if (updatedOrder.status === 'paid' || updatedOrder.status === 'cdek_created') {
                clearCart()
                sessionStorage.removeItem('shouldClearCart')
                sessionStorage.removeItem('pendingOrderId')
              }
            }
          } catch (paymentErr) {
            // Игнорируем ошибки проверки платежа
          }
        } 
        // Если заказ оплачен, но трек-номера еще нет, обновляем данные и очищаем корзину
        else if ((currentOrder?.status === 'paid' || currentOrder?.status === 'cdek_created' || currentOrder?.status === 'processing') && !currentOrder?.cdek_tracking_number) {
          setOrder(currentOrder)
          // Очищаем корзину при оплаченном заказе
          if (currentOrder.status === 'paid' || currentOrder.status === 'cdek_created') {
            clearCart()
            sessionStorage.removeItem('shouldClearCart')
            sessionStorage.removeItem('pendingOrderId')
        }
        } else if (currentOrder?.cdek_tracking_number) {
          // Если трек-номер уже есть, обновляем статус и останавливаем polling
          setOrder(currentOrder)
          // Очищаем корзину при завершенном заказе
          clearCart()
          sessionStorage.removeItem('shouldClearCart')
          sessionStorage.removeItem('pendingOrderId')
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }
        } catch (err) {
          // Игнорируем ошибки при polling
          console.log('Polling error:', err)
        }
      }, 5000) // Единый интервал 5 секунд
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [id, phone, requiresPhone]) // Добавляем phone и requiresPhone в зависимости

  const getStatusInfo = (status) => {
    const statuses = {
      pending: { text: 'Ожидает оплаты', icon: '⏳', type: 'pending' },
      paid: { text: 'Оплачен', icon: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), type: 'success' },
      processing: { text: 'Обрабатывается', icon: '◎', type: 'success' },
      cdek_created: { text: 'Передан в СДЭК', icon: '📦', type: 'success' },
      shipped: { text: 'В пути', icon: '→', type: 'success' },
      delivered: { text: 'Доставлен', icon: '✓', type: 'success' },
      cancelled: { text: 'Отменён', icon: '✕', type: 'error' },
    }
    return statuses[status] || { text: status, icon: '?', type: 'pending' }
  }

  const statusInfo = getStatusInfo(order?.status || 'pending')
  const isPaid = ['paid', 'processing', 'cdek_created', 'shipped', 'delivered'].includes(order?.status)

  if (loading) {
    return (
      <div style={{ padding: '48px 16px', textAlign: 'center' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #333',
          borderTopColor: '#ffdd2d',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 24px'
        }} />
        <div style={{ fontSize: '16px', color: '#666' }}>Загрузка заказа...</div>
      </div>
    )
  }

  // Форма ввода номера телефона
  if (requiresPhone) {
    return (
      <div style={{ padding: '48px 16px', maxWidth: '400px', margin: '0 auto' }}>
        <div style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px', color: '#000', textAlign: 'center' }}>
          Введите номер телефона
        </div>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '32px', textAlign: 'center' }}>
          Для просмотра заказа необходимо указать номер телефона, указанный при оформлении
        </div>
        
        <form onSubmit={handlePhoneSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="+7 (999) 999-99-99"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '16px',
                border: phoneError ? '2px solid #ff4444' : '1px solid #ddd',
                borderRadius: '8px',
                fontFamily: 'inherit'
              }}
              autoFocus
            />
            {phoneError && (
              <div style={{ color: '#ff4444', fontSize: '14px', marginTop: '8px' }}>
                {phoneError}
              </div>
            )}
          </div>
          
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '14px',
              background: '#000',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Просмотреть заказ
          </button>
        </form>
        
        {error && (
          <div style={{ color: '#ff4444', fontSize: '14px', marginTop: '16px', textAlign: 'center' }}>
            {error}
          </div>
        )}
      </div>
    )
  }

  if (error && !order) {
    return (
      <div style={{ padding: '48px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px', color: '#000' }}>
          ЗАКАЗ НЕ НАЙДЕН
        </div>
        <div style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
          {error}
        </div>
        <Link
          to="/"
          style={{
            display: 'inline-block',
            padding: '14px 32px',
            background: '#000',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          НА ГЛАВНУЮ
        </Link>
      </div>
    )
  }

  const handleCopyOrderNumber = () => {
    navigator.clipboard.writeText(order.order_number)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="order-page fade-in" ref={orderRef}>
      {/* Status */}
      <div className="checkout-status" style={{ minHeight: 'auto', padding: '32px 16px' }}>
        <div className={`checkout-status-icon ${statusInfo.type}`}>
          {statusInfo.icon}
        </div>
        <div className="checkout-status-title">
          {isPaid ? 'Заказ оформлен' : statusInfo.text}
        </div>
      </div>

      {isPaid && (
        <div
          style={{
            maxWidth: '480px',
            margin: '0 auto 24px',
            padding: '16px 18px',
            background: '#f7f7f7',
            borderRadius: '8px',
            textAlign: 'left',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#666',
              marginBottom: '10px',
            }}
          >
            Доставка и отслеживание
          </div>
          <p style={{ fontSize: '13px', lineHeight: 1.55, color: '#333', margin: '0 0 10px 0' }}>
            Передача отправлений в СДЭК выполняется <strong>один раз в неделю — по средам</strong>.
          </p>
          <p style={{ fontSize: '13px', lineHeight: 1.55, color: '#333', margin: 0 }}>
            Статус отправления можно отследить в{' '}
            <a
              href="https://lk.cdek.ru/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#000', fontWeight: 600, textDecoration: 'underline' }}
            >
              личном кабинете СДЭК
            </a>
            {' '}
            по номеру телефона, указанному при оформлении заказа, или по трек-номеру. Отслеживание также доступно на{' '}
            <a
              href="https://www.cdek.ru/ru/tracking"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#000', fontWeight: 500, textDecoration: 'underline' }}
            >
              cdek.ru/tracking
            </a>
            .
          </p>
        </div>
      )}

      {/* Order Number */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{
          fontSize: '10px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          color: '#666',
          marginBottom: '4px'
        }}>
          Номер заказа
        </div>
        <div 
          onClick={handleCopyOrderNumber}
          style={{
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {order.order_number}
          <span style={{ fontSize: '14px', opacity: 0.6 }}>
            {copied ? '✓' : '❐'}
          </span>
        </div>
        {copied && (
          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
            Скопировано
          </div>
        )}
        
        {/* Трек-номер CDEK */}
        {order.cdek_tracking_number ? (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              fontSize: '10px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#666',
              marginBottom: '4px'
            }}>
              Трек-номер СДЭК
            </div>
            <a 
              href={`https://www.cdek.ru/ru/tracking?order_id=${order.cdek_tracking_number}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard.writeText(order.cdek_tracking_number)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              style={{
                fontSize: '16px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                color: '#000',
                textDecoration: 'underline',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {order.cdek_tracking_number}
              <span style={{ fontSize: '12px', opacity: 0.6 }}>↗</span>
            </a>
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
              Нажмите для отслеживания
            </div>
          </div>
        ) : (order.status === 'paid' || order.status === 'cdek_created' || order.cdek_order_uuid) && (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              fontSize: '10px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#666',
              marginBottom: '4px'
            }}>
              Трек-номер СДЭК
            </div>
            <div style={{
              fontSize: '14px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              Ожидается присвоение...
            </div>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="order-section">
        <div className="order-section-header">Товары</div>
        <div className="order-section-content">
          {order.items?.map((item, idx) => (
            <div key={idx} className="order-item">
              <div className="order-item-thumb">
                {item.product?.images?.[0]?.image && (
                  <img
                    src={item.product.images[0].image.startsWith('http') 
                      ? item.product.images[0].image 
                      : `${API_URL}${item.product.images[0].image}`}
                    alt={item.product.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </div>
              <div className="order-item-info">
                <div className="order-item-name">{item.product?.name}</div>
                <div className="order-item-meta">
                  {item.variant?.size && `Размер: ${item.variant.size}`}
                  {item.variant?.size && ' • '}
                  Кол-во: {item.quantity}
                </div>
              </div>
              <div className="order-item-price">{formatPrice(item.subtotal)} ₽</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recipient */}
      {order.delivery_address && (
        <div className="order-section">
          <div className="order-section-header">Получатель</div>
          <div className="order-section-content">
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
              {order.delivery_address.full_name}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              {order.delivery_address.phone}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {order.delivery_address.email}
            </div>
          </div>
        </div>
      )}

      {/* PVZ */}
      {order.pvz_code && (
        <div className="order-section">
          <div className="order-section-header">Пункт выдачи СДЭК</div>
          <div className="order-section-content">
            {order.pvz_name && (
              <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                {order.pvz_name}
              </div>
            )}
            
            {order.pvz_address ? (
              <a
                href={order.pvz_latitude && order.pvz_longitude 
                  ? `https://yandex.ru/maps/?pt=${order.pvz_longitude},${order.pvz_latitude}&z=17&l=map&text=СДЭК`
                  : `https://yandex.ru/maps/?text=СДЭК+${encodeURIComponent(order.pvz_address)}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="address-link"
              >
                <span className="address-link-icon">📍</span>
                <div className="address-link-text">
                  <div className="address-link-address">{order.pvz_address}</div>
                  <div className="address-link-hint">Открыть в Яндекс Картах →</div>
                </div>
              </a>
            ) : (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Код ПВЗ: {order.pvz_code}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Total */}
      <div className="order-total">
        <span className="order-total-label">Итого</span>
        <span className="order-total-value">{formatPrice(order.total_amount)} ₽</span>
      </div>

      {/* Contact */}
      <a 
        href="https://t.me/crysquad_store"
        target="_blank"
        rel="noopener noreferrer"
        className="contact-btn"
      >
        <span>✈</span>
        Связаться с нами
      </a>

      {/* Continue Shopping */}
      <Link to="/" className="continue-btn">
        Продолжить покупки
      </Link>
    </div>
  )
}

export default OrderPage
